import type { CityConfig } from "../city-template/types";

export const enidConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Enid",
  state: "Oklahoma",
  stateAbbr: "OK",
  slug: "enid",
  route: "/cities/enid",
  title: "Emergency Passport Enid, OK | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Enid, OK — as fast as 24 hours. Serving Vance AFB, Northwest Oklahoma wheat country. A+ BBB rated registered State Dept. courier. Best price guarantee. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Enid, OK",
  heroSubtitle: "Vance AFB deployment with 5 days notice? Wheat harvest celebration trip to Germany and passport expired? Business trip to London from Northwest Oklahoma with no valid documents? We help Enid military families and wheat country residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Enid%20Oklahoma%20wheat%20fields%20golden%20harvest%20stretching%20to%20horizon%20grain%20elevators%20and%20silos%20historic%20downtown%20Enid%20with%20Cherokee%20Strip%20Conference%20Center%20Vance%20Air%20Force%20Base%20T-38%20training%20jets%20in%20formation%20warm%20amber%20sunset%20light%20Great%20Plains%20landscape%20professional%20drone%20photography&width=1800&height=700&seq=enid-ok-hero-2026&orientation=landscape",
  heroImageAlt: "Enid Oklahoma wheat fields and Vance AFB with golden sunset over the Great Plains",
  geoPosition: "36.3956;-97.8784",
  geoPlacename: "Enid, Oklahoma",
  regionalAgency: {
    name: "Dallas Passport Agency",
    address: "1100 Commerce Street, Suite 900, Dallas, TX 75242",
    distance: "Located in downtown Dallas — approximately 340 miles south of Enid via I-35",
  },
  nearbyCitiesZips: [
    { name: "Enid", zips: "73701, 73703, 73705" },
    { name: "Woodward", zips: "73801" },
    { name: "Alva", zips: "73717" },
    { name: "Ponca City", zips: "74601, 74602" },
    { name: "Stillwater", zips: "74074, 74075" },
    { name: "Guthrie", zips: "73044" },
    { name: "Kingfisher", zips: "73750" },
    { name: "Hennessey", zips: "73742" },
    { name: "Waukomis", zips: "73773" },
    { name: "Garber", zips: "73738" },
    { name: "Fairview", zips: "73737" },
    { name: "Watonga", zips: "73772" },
    { name: "Perry", zips: "73077" },
    { name: "Cherokee", zips: "73728" },
    { name: "Medford", zips: "73759" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Vance AFB and Northwest Oklahoma departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Vance AFB & Wheat Country", desc: "Serving Enid, Woodward, Alva, Ponca City, and Northwest Oklahoma" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Oklahoma City, OK", route: "/cities/oklahoma-city" },
    { name: "Fast passport renewal in Tulsa, OK", route: "/cities/tulsa" },
    { name: "Emergency passport help in Stillwater, OK", route: "/cities/stillwater" },
    { name: "Urgent passport services in Edmond, OK", route: "/cities/edmond" },
    { name: "Same-day passport help in Dallas, TX", route: "/cities/dallas" },
    { name: "Passport courier services in Wichita, KS area", route: "/states/kansas" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Enid remotely via secure courier. We do not have a walk-in office in Enid — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Garfield County and Northwest Oklahoma.",
  fpcOfficeAddress: "525 N Tryon St., Charlotte, NC 28202",
  fpcOfficeDistance: "Serving all of Garfield County and the Northwest Oklahoma region including Woodward, Woods, Alfalfa, and Grant counties",
  serviceAreaIntro: "From the T-38 Talon jets soaring over Vance Air Force Base to the endless golden wheat fields of the Cherokee Strip, from the historic grain elevators and railroad heritage of downtown Enid to the oil-rich communities of Woodward and Alva, from the pioneer spirit of the Cherokee Outlet land run to the agricultural towns of Hennessey, Waukomis, and Kingfisher — Fast Passport Center serves the entire Northwest Oklahoma region. We regularly help military families, oil industry professionals, and agricultural travelers get their passports fast without the 340-mile drive to Dallas.",
  nearbyCitiesIntro: "We serve all Enid neighborhoods and the surrounding Northwest Oklahoma area including Woodward, Alva, Ponca City, Stillwater, Guthrie, and Kingfisher. If you are anywhere in Garfield County, Woodward County, or Alfalfa County, we have you covered.",
  howItWorksIntro: "Most Enid travelers complete everything online and ship documents overnight. No need to drive to Dallas or wait in line at the regional agency.",
  mistakesIntro: "Enid travelers often face a 5-hour drive to the nearest regional passport agency in Dallas. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Enid military families and Northwest Oklahoma travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Enid? Here are the most common ones we hear from Vance AFB and Northwest Oklahoma travelers.",
  statePageRoute: "/states/oklahoma",
  statePageName: "Oklahoma",
  dmvWebsite: "https://oklahoma.gov/dps.html",
  dmvLabel: "Oklahoma DPS — Driver License Services",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Enid travelers with immediate departure needs — as fast as 24 hours for deployment or urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-bank-card-line",
      title: "Passport Card",
      description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean.",
      bullets: ["REAL ID compliant alternative", "Fits in your wallet", "Land/sea border crossing approved"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
    {
      icon: "ri-error-warning-line",
      title: "Damaged Passport",
      description: "Replacement for water-damaged, torn, or otherwise mutilated passports that are no longer valid for travel.",
      bullets: ["Damage assessment guidance", "DS-11 replacement process", "Supporting documentation help"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-file-copy-line",
      title: "Second Passport",
      description: "Obtain a second valid U.S. passport for frequent international travelers who need overlapping visas.",
      bullets: ["For frequent business travelers", "Overlapping visa support", "Expedited processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Enid travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Enid with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Enid and Vance AFB residents with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
    { name: "Passport Card", description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean." },
    { name: "Damaged Passport", description: "Replacement for water-damaged, torn, or mutilated passports requiring DS-11 replacement." },
    { name: "Second Passport", description: "Second valid U.S. passport for frequent international travelers with overlapping visa needs." },
  ],
  testimonials: [
    {
      name: "Capt. David W.",
      location: "Vance AFB, OK",
      rating: 5,
      text: "Got short-notice deployment to the Middle East. Fast Passport Center processed my passport in 28 hours. The agent knew exactly what Vance personnel needed. Outstanding.",
      service: "Emergency Passport",
    },
    {
      name: "Shirley P.",
      location: "Enid, OK",
      rating: 5,
      text: "Our Germany trip to see the Christmas markets was in 6 days when we found expired passports. Fast Passport Center had them renewed in 48 hours. Enid to Bavaria — incredible!",
      service: "Passport Renewal",
    },
    {
      name: "Tom B.",
      location: "Woodward, OK",
      rating: 5,
      text: "Oil business trip to Dubai came up fast. Fast Passport Center got my passport in 2 days. Never had to leave Woodward. Best service in Northwest Oklahoma.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "enid-ok-1",
      name: "Enid Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "115 W Broadway Ave",
      city: "Enid",
      state: "OK",
      zip: "73701",
      phone: "(580) 233-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Enid",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Enid facility. Appointments strongly recommended. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "enid-ok-2",
      name: "Garfield County Court Clerk",
      type: "County Court Clerk Passport Acceptance",
      address: "114 W Broadway Ave, Suite 202",
      city: "Enid",
      state: "OK",
      zip: "73701",
      phone: "(580) 237-0232",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Enid Courthouse",
      website: "https://www.garfieldcountyok.com",
      websiteLabel: "Garfield County Court Clerk",
      notes: "Accepts passport applications. Good alternative to post office. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "enid-ok-3",
      name: "Woodward Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1120 Main St",
      city: "Woodward",
      state: "OK",
      zip: "73801",
      phone: "(580) 256-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "85 miles west of Enid",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Woodward County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "enid-ok-4",
      name: "Ponca City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "402 E Grand Ave",
      city: "Ponca City",
      state: "OK",
      zip: "74601",
      phone: "(580) 762-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "45 miles east of Enid",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Kay County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "enid-ok-5",
      name: "Kingfisher Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 S Main St",
      city: "Kingfisher",
      state: "OK",
      zip: "73750",
      phone: "(405) 375-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "45 miles south of Enid",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Kingfisher County. Full passport services available.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Dallas", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Enid photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Garfield County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Enid applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Dallas regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Vance AFB families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Enid travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Enid." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Enid address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Enid before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Enid for travelers with urgent international flights or military deployment. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5-hour drive to Dallas." },
    { q: "How fast can I renew my passport in Enid?", a: "Fast Passport Center offers fast passport renewal in Enid in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Enid?", a: "The nearest regional passport agency to Enid is the Dallas Passport Agency, approximately 340 miles south via I-35. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Enid without any in-person agency visit required." },
    { q: "Does Vance AFB offer passport services for military families?", a: "Vance AFB military families can use Fast Passport Center's expedited courier service for all passport needs, including deployment-related processing. We understand military timelines and training schedules. Our 24–48 hour emergency service is ideal for short-notice orders from Northwest Oklahoma." },
    { q: "Do you serve the entire Northwest Oklahoma region?", a: "Absolutely. Fast Passport Center serves the entire Northwest Oklahoma region including Enid, Woodward, Alva, Ponca City, Kingfisher, and surrounding areas. Our courier service works seamlessly from any address in Garfield County, Woodward County, or Alfalfa County." },
    { q: "Can I expedite a child passport in Enid?", a: "Yes. Fast Passport Center expedites child passports in Enid for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Northwest Oklahoma families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Enid with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Dallas Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Enid", address: "5505 W Owen K Garriott Rd, Enid, OK 73703", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com" },
    { name: "Walgreens — Enid", address: "929 W Owen K Garriott Rd, Enid, OK 73701", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com" },
    { name: "Walmart Supercenter — Woodward", address: "4000 Williams Ave, Woodward, OK 73801", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com" },
    { name: "Walmart Supercenter — Ponca City", address: "3250 N 14th St, Ponca City, OK 74601", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com" },
  ],
  dmvLocations: [
    {
      name: "Oklahoma DPS — Enid",
      address: "1020 E Owen K Garriott Rd, Enid, OK 73701",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "Oklahoma DPS — Woodward",
      address: "1611 Main St, Woodward, OK 73801",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Woodward County. REAL ID available. Appointment recommended.",
      featured: false,
    },
    {
      name: "Oklahoma DPS — Ponca City",
      address: "400 E Central Ave, Ponca City, OK 74601",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Kay County. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "WDG",
      name: "Enid Woodring Regional Airport",
      distance: "4 miles east of downtown Enid",
      address: "1026 S 66th St, Enid, OK 73701",
      note: "Regional airport serving Enid and Vance AFB. About 8 minutes from downtown. General aviation and charter services.",
      flightUrl: "https://www.enid.org/airport",
      flightLabel: "WDG Airport",
      featured: true,
    },
    {
      code: "OKC",
      name: "Will Rogers World Airport",
      distance: "95 miles south of Enid",
      address: "7100 Terminal Dr, Oklahoma City, OK 73159",
      note: "Major Oklahoma airport with direct flights nationwide. About 1 hour 30 minutes from Enid.",
      flightUrl: "https://flyokc.com/",
      flightLabel: "OKC Airport",
      featured: false,
    },
    {
      code: "ICT",
      name: "Wichita Dwight D. Eisenhower National Airport",
      distance: "140 miles north of Enid",
      address: "2277 Eisenhower Airport Pkwy, Wichita, KS 67209",
      note: "Regional hub serving Kansas and northern Oklahoma. About 2 hours from Enid.",
      flightUrl: "https://www.flywichita.com/",
      flightLabel: "ICT Airport",
      featured: false,
    },
  ],
};